import React from "react";
import classNames from "classnames";
import plur from "plur";
import { Card, Flex, Text } from "@jsq/ds/core";
import { FormCheckbox, FormTextInput, Form } from "@jsq/ds/forms";
import { stackedSm, stackedMd } from "../../../../../common/themes/spacing_styles_vanilla.css";
import { oxfordComma } from "../../../../../common/utils";
import { type AchConfirmFormProps } from "./interfaces";
import { type AchConfirmFormInputs, achConfirmValidation } from "./schema";
import * as styles from "./styles_vanilla.css";

export const AchConfirmationForm = ({ accounts, formRef, onSubmit }: AchConfirmFormProps) => {
  const investmentEntityNames = accounts
    .flatMap((account) =>
      account.node.positions?.edges.map((position) => position.node.investmentEntity.name),
    )
    .filter((name): name is string => !!name);

  const investmentEntityNamesString = oxfordComma(investmentEntityNames);
  const numInvestmentEntities = investmentEntityNames.length;
  const pEntity = plur("Entity", numInvestmentEntities);
  const possessiveEntity = `${pEntity}${investmentEntityNames.length > 1 ? "'" : "'s"}`;
  const pAccount = plur("account", accounts.length);
  const accountNames = oxfordComma(accounts.map((account) => account.node.name));

  return (
    <Form
      innerRef={formRef}
      onSubmit={async (values: AchConfirmFormInputs) => {
        await onSubmit(values.achConfirm.fullName);
      }}
      submitOnlyChangedValues={false}
      initialValues={{
        achConfirm: {
          fullName: "",
          acceptance: false,
        },
      }}
      validationSchema={achConfirmValidation}
    >
      <Card
        className={classNames(styles.container, "inset-lg")}
        data-cy="investment-accounts-ach-confirm"
      >
        <Text variant="strong-md" className={stackedSm}>
          Enter your full legal name in the space below to confirm this change to your payment
          instructions
        </Text>
        <Text variant="regular-sm" className={stackedSm}>
          {`The undersigned hereby authorizes ${investmentEntityNamesString} (the "Investment ${pEntity}") to
            initiate electronic credit entries for the purpose of making distribution payments
            from the Investment ${pEntity}, and if necessary, debit entries and adjustments for
            any credit entries in error, to the following ${pAccount}: ${accountNames}`}
        </Text>
        <Text variant="regular-sm" className={stackedMd}>
          {`This authority and authorization is to remain in full force and effect until the
            undersigned has issued written notification of its termination by updating these
            payment instructions in the investor portal or contacting the relevant investment
            manager directly, in such time and in such manner as to
            afford the Investment ${possessiveEntity} financial institution(s) a reasonable opportunity to
            act on it. `}
          <u>
            {`The undersigned agrees that Investment ${pEntity} are authorized to rely on
          any updates to the foregoing account information made through the investor portal
          and this consent shall remain valid and in full force and effect following any such
          updates.`}
          </u>
        </Text>
        <div className={stackedMd}>
          <FormCheckbox
            wrapperClassName={styles.checkboxWrapper}
            name="achConfirm.acceptance"
            label="I accept the terms of this agreement"
          />
        </div>
        <Flex flexDirection="row" alignItems="center">
          <FormTextInput
            placeholder="Full name"
            name="achConfirm.fullName"
            className="inline-lg"
            required
            isFullWidth
          />
          <Flex flexGrow={1}>
            <Text variant="regular-md" className={styles.signatureDate}>{`Dated: ${new Date(
              Date.now(),
            ).toLocaleDateString("en-US", {
              dateStyle: "long",
            })}`}</Text>
          </Flex>
        </Flex>
      </Card>
    </Form>
  );
};

export default AchConfirmationForm;
